<template>
    <div>
        <DAlert
            v-if="isFulfillmentPackage === true"
            color="secondary"
            class="break-text"
        >
            {{ __('products.product_packages.form.note') }}
        </DAlert>
        <DFormRow
            :label="__('products.product_packages.form.name')"
            :is-required="true"
            :error="errors.name"
            is-vertical
        >
            <DInput
                id="name"
                v-model="form.name"
                :placeholder="__('products.product_packages.form.name')"
                :is-valid="isFormFieldValid('name')"
                class="width-name"
                maxlength="255"
            />
        </DFormRow>
        <DListGroup>
            <DListGroupItem class="d-flex justify-content-between align-items-center group-header">
                <div class="div-product-group required">
                    {{ __('products.product_packages.form.product_group') }}
                </div>
                <div class="div-product required">
                    {{ __('products.product_packages.form.product') }}
                </div>
                <div
                    v-if="!isFulfillmentPackage"
                    class="div-product-plan"
                >
                    {{ __('products.product_packages.form.plan') }}
                </div>
                <div class="div-actions" />
            </DListGroupItem>
            <VueNestable
                v-model="entitiesData"
                :max-depth="contractMaxNestingLevel"
                key-prop="key"
                children-prop="children"
            >
                <template #default="{ item }">
                    <DListGroupItem
                        class="d-flex justify-content-between align-items-center group-body"
                    >
                        <div class="div-product-selection">
                            <ProductPackagesSelection
                                :item="item"
                                :disabled="disabled"
                                :errors="getErrors(item.key)"
                                :is-fulfillment-package="isFulfillmentPackage"
                                @set-product-groups="setProductGroups"
                                @reset-product-id="resetIds"
                                @display-retired="displayRetired"
                                @product-group-update="handleGroupChange"
                                @product-update="handleProductChange"
                                @plan-update="handlePlanChange"
                            />
                        </div>
                        <div
                            class="div-actions"
                            :class="getErrorsExist(item.key) ? 'div-actions-with-errors' : ''"
                        >
                            <div class="actions-body">
                                <!-- Handler -->
                                <VueNestableHandle :item="item">
                                    <div class="meta">
                                        <div>
                                            <DButton
                                                icon="drag-variant"
                                                mini
                                                drag-and-drop-btn
                                            />
                                        </div>
                                        <div />
                                    </div>
                                </VueNestableHandle>
                                <div class="meta">
                                    <div>
                                        <DButton
                                            icon="trash-can-outline"
                                            mini
                                            :disabled="isOnlyEntity(item)"
                                            @click="deleteRow(item, entitiesData)"
                                        />
                                    </div>
                                    <div />
                                </div>
                            </div>
                        </div>
                    </DListGroupItem>
                </template>
            </VueNestable>
        </DListGroup>
        <DFormControls
            :cancel-route="cancelRoute"
            :dirty="form.isDirty"
            @submit="submitForm"
        >
            <template #additional_button_before_submit>
                <DFab
                    :color="'secondary'"
                    icon="plus"
                    :label="__('products.product_packages.form.add_entity')"
                    @click="addNewRow()"
                />
            </template>
        </DFormControls>
    </div>
</template>

<script>
import { VueNestable, VueNestableHandle } from '@sparkz-community/vue3-nestable'
import ProductPackagesSelection from '~@/Components/Products/ProductPackages/ProductPackagesSelection'

export default {
    name: 'ProductPackageForm',
    components: { ProductPackagesSelection, VueNestable, VueNestableHandle },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        productPackage: {
            type: Object,
            default: () => { return {} }
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false
        },
        isFulfillmentPackage: {
            type: Boolean,
            default: () => { return false }
        },
        contractMaxNestingLevel: {
            type: Number,
            default: () => { return 3 }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                id: this.productPackage?.id ?? null,
                name: this.productPackage?.name ?? null,
                entities: [],
                isFulfillmentPackage: this.isFulfillmentPackage
            }),
            fakeKey: this.productPackage?.key ?? 0,
            entitiesData: this.productPackage?.entities ?? [this.getNewRow()]
        }
    },
    computed: {
        cancelRoute () {
            return this.$route('products.product-packages.index')
        }
    },
    methods: {
        submitForm () {
            if (this.entitiesData.length === 0) {
                return
            }

            this.form.entities = this.flattenEntities(this.entitiesData)

            this.$emit('form-submitted', this.form)
        },
        flattenEntities (entities, parentKey = null) {
            if (entities.length === 0) {
                return {}
            }

            let result = {}

            for (const entity of entities) {
                const tmpEntity = JSON.parse(JSON.stringify(entity))
                delete tmpEntity.children

                tmpEntity.parent_key = parentKey

                result[entity.key] = tmpEntity
                result = { ...result, ...this.flattenEntities(entity.children, entity.key) }
            }

            return result
        },
        getNewRow () {
            return {
                key: 0,
                id: null,
                product_group_id: '0',
                product: null,
                product_id: null,
                product_plan_id: null,
                available_groups: [],
                available_products: [],
                available_plans: [],
                children: [],
                display_retired: false
            }
        },
        addNewRow () {
            const newEmptyRow = this.getNewRow()
            newEmptyRow.key = ++this.fakeKey
            this.entitiesData.push(newEmptyRow)
        },
        deleteRow (item, entities, parentEntity = null) {
            for (const entityItem of entities) {
                if (entityItem.key === item.key) {
                    // If entity have parent entity and children, add children to parent entity
                    if (parentEntity !== null && entityItem.children.length > 0) {
                        for (const child of entityItem.children) {
                            parentEntity.children.push(child)
                        }
                    }

                    entities.splice(entities.indexOf(item), 1)

                    return
                }

                if (entityItem.children.length > 0) {
                    this.deleteRow(item, entityItem.children, entityItem)
                }
            }
        },
        isOnlyEntity (item) {
            return this.entitiesData.length === 1 && this.entitiesData[0].key === item.key
        },
        displayRetired (values) {
            const entity = this.findNested(this.entitiesData, values.entity.key)

            if (entity !== null) {
                entity.display_retired = values.retired
            }
        },
        resetIds (item) {
            const entity = this.findNested(this.entitiesData, item.key)

            if (entity !== null) {
                entity.product_group_id = '0'
                entity.product_id = null
                entity.product_plan_id = null
            }
        },
        setProductGroups (values) {
            const entity = this.findNested(this.entitiesData, values.entity.key)

            if (entity !== null) {
                entity.available_groups = values.availableGroups
            }
        },
        handleGroupChange (values) {
            const entity = this.findNested(this.entitiesData, values.entity.key)

            if (entity !== null) {
                entity.available_products = values.availableProducts
                entity.available_plans = []
                entity.product_group_id = values.groupId
                entity.product = null
                entity.product_id = null
                entity.product_plan_id = null
            }
        },
        handleProductChange (values) {
            const entity = this.findNested(this.entitiesData, values.entity.key)

            if (entity !== null) {
                entity.available_plans = values.availablePlans
                entity.product = values.product
                entity.product_id = values.product.value
                entity.product_plan_id = null
            }
        },
        handlePlanChange (values) {
            const entity = this.findNested(this.entitiesData, values.entity.key)

            if (entity !== null) {
                entity.product_plan_id = values.planId
            }
        },
        findNested (entities, key) {
            for (const entity of entities) {
                if (entity.key === key) {
                    return entity
                }

                if (entity.children.length > 0) {
                    const match = this.findNested(entity.children, key)

                    if (match !== null) {
                        return match
                    }
                }
            }

            return null
        },
        getErrorsExist (key) {
            return typeof this.errors[`entities.${key}.product_group_id`] !== 'undefined' ||
                typeof this.errors[`entities.${key}.product_id`] !== 'undefined' ||
                typeof this.errors[`entities.${key}.product_plan_id`] !== 'undefined'
        },
        getErrors (key) {
            const errors = {}

            if (typeof this.errors[`entities.${key}.product_group_id`] !== 'undefined') {
                errors.product_group_id = this.errors[`entities.${key}.product_group_id`]
            }

            if (typeof this.errors[`entities.${key}.product_id`] !== 'undefined') {
                errors.product_id = this.errors[`entities.${key}.product_id`]
            }

            if (typeof this.errors[`entities.${key}.product_plan_id`] !== 'undefined') {
                errors.product_plan_id = this.errors[`entities.${key}.product_plan_id`]
            }

            return errors
        }
    }
}
</script>

<style lang="scss">
/*
* Style for nestable
*/
.nestable {
    position: relative;
}
.nestable-rtl {
    direction: rtl;
}
.nestable .nestable-list {
    margin: 0;
    padding: 0 0 0 40px;
    list-style-type: none;
}
.nestable-rtl .nestable-list {
    padding: 0 40px 0 0;
}
.nestable > .nestable-list {
    padding: 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
    margin-top: 0;
}
.nestable-item {
    position: relative;
}
.nestable-item.is-dragging .nestable-list {
    pointer-events: none;
}
.nestable-item.is-dragging * {
    opacity: 0;
    filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(106, 127, 233, 0.274);
    border: 1px dashed rgb(73, 100, 241);
    -webkit-border-radius: 5px;
    border-radius: 5px;
}
.nestable-drag-layer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
    left: auto;
    right: 0;
}
.nestable-drag-layer > .nestable-list {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
    padding: 0;
}
.nestable-handle {
    display: inline;
}
.group-header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        font-weight: bold;
    }
}
.group-body {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        & > div {
            background: var(--colorSecondary);
            border-radius: 5px;
            height: auto;
        }
    }
}
.div-product-group {
    width: 42%;
}
.div-product {
    width: 35%;
}
.div-product-plan {
    width: 23%;
}
.div-product-selection {
    width: 93%;
}
.div-actions {
    width: 7%;
}
.div-actions-with-errors {
    margin-bottom: 20px;
}
.actions-body {
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    padding: 0 !important;
    background: var(--colorAppSurface) !important;
}
.width-name {
    width: 40%
}
.required::after {
    color: var(--colorDanger);
}
.break-text {
    white-space: break-spaces !important;
}
</style>
